import './App.css';
import React, { createContext, useEffect, useRef, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { DFConnection } from './components/connections/df_connection';
import { LoginForm } from './components/forms/login_form';
import { Provider, useDispatch, useSelector } from 'react-redux';
import globalsStore from './redux/globalsStore';
import { DndContext } from '@dnd-kit/core';
import Header from './components/menu2/header';
import { handleLogoff } from './components/forms/logoff_form/logoff_form';
import { selectForcePasswordChange, selectTSUserId, setIsMarketController, setRoleId } from './redux/globalsSlice';
import { TSConnection1 } from './components/connections/ts_connection1';
import { DispatchTransactionEvent } from './components/events/events';
import { EVENT_TOPIC_FOR_TRANSACTION_SERVER, EVENT_TYPE_PRINT_TABLE, JSON_KEY_MESSAGE_TYPE, JSON_KEY_SUBMITTER, 
          JSON_KEY_TABLES, PRINT_ALL } from './common/common';
import { MSG_TYPE_PRINT_TABLE } from './common/msg_types';
import InactivityLogout from './common/timeout';
import { ChangePasswordForm } from './components/forms/change_password_form';
import Dashboard, { getDefaultLayout } from './components/dashboard/dashboard';
import { selectIsMarketController } from './redux/globalsSlice';
import { loadPanelPositionsForUser, loadTableStatesForUser } from './redux/panelPositionsSlice';

export const GlobalData = createContext();

const App = () => {
  return (
    <Provider store={globalsStore}>
        <MainComponent />
    </Provider>
  );
};

const MainComponent = () => {
  const all_in_one_server = window.APP_CONFIG.ALL_IN_ONE_SERVER;
  // console.log("App.js : ", all_in_one_server);
  const [tsConnectionStatus, loggedOn, setLoggedOn] = TSConnection1();
  const [dfConnectionStatus] = all_in_one_server ? 'Closed' : DFConnection();
  const dispatch = useDispatch();
  const tsUserId = useSelector(selectTSUserId);
  const tsUserIdRef = useRef(tsUserId);
  const forcePasswordChange = useSelector(selectForcePasswordChange);
  const forcePasswordChangeRef = useRef(selectForcePasswordChange);
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const dashboardRef = useRef();
  const isMarketController = useSelector(selectIsMarketController);

  // Create handlers that call the dashboard methods
  const handleSaveWorkspace = () => {
    dashboardRef.current?.saveWorkspace?.();
  };

  const handleResetWorkspace = () => {
    // console.log('handleResetWorkspace')
    dashboardRef.current?.resetToDefault?.();
  };

  const handleExportWorkspace = () => {
    dashboardRef.current?.saveWorkspaceToFile?.();
  };

  const handleImportWorkspace = () => {
    document.getElementById('workspace-import-trigger')?.click();
  };

  useEffect(() => {
    forcePasswordChangeRef.current = forcePasswordChange;
  }, [forcePasswordChange]);
    
  useEffect(() => {
    tsUserIdRef.current = tsUserId;
  }, [tsUserId]);
    
  const handleLogout = async () => {
    // Save workspace before clearing storage
    if (dashboardRef.current?.saveWorkspaceToStorage) {
      dashboardRef.current.saveWorkspaceToStorage();
    }

    await handleLogoff();
  };
  
  const handlePrintTables = () => {
    DispatchTransactionEvent(
        EVENT_TOPIC_FOR_TRANSACTION_SERVER,
        EVENT_TYPE_PRINT_TABLE,
        {
          [JSON_KEY_SUBMITTER]: tsUserId,
          [JSON_KEY_MESSAGE_TYPE]: MSG_TYPE_PRINT_TABLE,
          [JSON_KEY_TABLES]: PRINT_ALL,
        }
    );
  };

  useEffect(() => {
    dispatch(setRoleId('TRADER'));
    dispatch(setIsMarketController(false));
  }, [dispatch]);

  useEffect(() => {
    document.title = 'CTS Workstation';
  }, []);

  if (window.WebSocket.toString().includes('[native code]') === false) {
    console.warn("⚠️ WebSocket has been overridden!", window.WebSocket);
  }

  useEffect(() => {
    if (loggedOn && tsUserId) {
      dispatch(loadPanelPositionsForUser(tsUserId, getDefaultLayout()));
      dispatch(loadTableStatesForUser(tsUserId)); 
    }
  }, [loggedOn, tsUserId, dispatch]);

  return (
    <>
      <InactivityLogout />
      
      {/* Connecting screens */}
      {all_in_one_server ? (
        <div style={{ display: ((tsConnectionStatus === 'Open') && ((dfConnectionStatus === 'Open') || all_in_one_server)) ? 'none' : 'block' }}>
          <form className="window-small-center">
            <label className="center">Trying to connect...</label>
            <p></p>
            <p className="center">Refresh the page or contact the CTS administration.</p>
          </form>
        </div>
      ) : (
        <div style={{ display: ((tsConnectionStatus === 'Open') && ((dfConnectionStatus === 'Open') || all_in_one_server)) ? 'none' : 'block' }}>
          <form className="window-small-center">
            <label className="center">Tryinng to connect both datafeed and transaction lines</label>
            <p></p>
            <p className="center">This window may disappear once connected, if window persists refresh the page or check that the CTS server is running or network connection is ok.</p>
          </form>
        </div>
      )}

      {/* Change password form if required */}
      {forcePasswordChange && (<ChangePasswordForm setLoggedOn={setLoggedOn} forced={true} />)}

      {/* Logon Form */}
      <div style={{
        display: (!forcePasswordChange && (tsConnectionStatus === 'Open') && ((dfConnectionStatus === 'Open') || all_in_one_server) && !loggedOn) ? 'block' : 'none'
      }}>
        <LoginForm />
      </div>

      {/* Main App Screen */}
      <div style={{
        display: (!forcePasswordChange && (tsConnectionStatus === 'Open') && ((dfConnectionStatus === 'Open') || all_in_one_server) && loggedOn) ? 'block' : 'none'
      }}>
        <DndContext>
          {showChangePasswordForm && (
            <div style={{
              position: 'fixed',
              top: '15%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              padding: '0px',
              border: '2px solid black',
              zIndex: 2000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              minWidth: '400px',
            }}>
              <ChangePasswordForm
                setLoggedOn={setLoggedOn}
                onClose={() => setShowChangePasswordForm(false)}
                forced={false}
                style={{
                  maxWidth: '400px',
                  margin: '0 auto',
                  marginTop: '0px',
                  padding: '0',
                }}
              />
            </div>
          )}

          <div className='header-container'>
            <Header
              onPrintTables={() => handlePrintTables()}
              onLogout={handleLogout}
              username={tsUserIdRef.current}
              onShowChangePassword={() => setShowChangePasswordForm(true)}
              onSaveWorkspace={handleSaveWorkspace}
              onResetWorkspace={handleResetWorkspace}
              onExportWorkspace={handleExportWorkspace}
              onImportWorkspace={handleImportWorkspace}
              isMarketController={isMarketController}
            />
          </div>

          {/* Hidden file input for import */}
          <input
            id="workspace-import-trigger"
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={(e) => {
              dashboardRef.current?.loadWorkspaceFromFile?.(e);
              e.target.value = '';
            }}
          />

          <div className="App">
            {loggedOn && tsUserId ? (
              <Dashboard
                ref={dashboardRef}
                key={tsUserId}   // force remount when user changes
                userId={tsUserId}
              />
            ) : (
              <div style={{ textAlign: "center", marginTop: "2rem" }}>
                <p>Loading workspace...</p>
              </div>
            )}
          </div>

        </DndContext>
      </div>
    </>
  );
}

export default App;